import { useEffect, type RefObject } from 'react';

export function pickerKeyAction(key: string, index: number, count: number) {
  if (key === 'Escape') return { kind: 'close' as const };
  // With no matching option, Enter is not ours: the composer keeps its own
  // submit so a message ending in ` /word` or ` @word` can still be sent.
  if (key === 'Enter')
    return count ? { kind: 'choose' as const, index: Math.min(index, count - 1) } : null;
  if ((key === 'ArrowDown' || key === 'ArrowUp') && count)
    return {
      kind: 'move' as const,
      index: (index + (key === 'ArrowDown' ? 1 : -1) + count) % count,
    };
  return null;
}

/** Keep slash/mention selection in the existing composer instead of submitting the token. */
export function useContextPickerKeyboard({
  open,
  anchor,
  picker,
  count,
  selected,
  onMove,
  onChoose,
  onClose,
}: {
  open: boolean;
  anchor: RefObject<HTMLDivElement | null>;
  picker: RefObject<HTMLDivElement | null>;
  count: number;
  selected: number;
  onMove: (index: number) => void;
  onChoose: (index: number) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!open) return;
    const composer = anchor.current?.closest('.aui-composer-root');
    const onKey = (event: KeyboardEvent) => {
      const target = event.target;
      if (
        !(target instanceof HTMLElement) ||
        !target.matches('input,textarea') ||
        !(composer?.contains(target) || picker.current?.contains(target)) ||
        event.isComposing ||
        event.shiftKey ||
        event.ctrlKey ||
        event.metaKey ||
        event.altKey
      )
        return;
      if (event.key === 'Tab') {
        onClose();
        return;
      }
      const action = pickerKeyAction(event.key, selected, count);
      if (!action) return;
      event.preventDefault();
      event.stopPropagation();
      if (action.kind === 'close') onClose();
      else if (action.kind === 'move') onMove(action.index);
      else onChoose(action.index);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, anchor, picker, count, selected, onMove, onChoose, onClose]);
}
